import React from "react";
import {
  Button,
  Checkbox,
  Dropdown,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
} from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Locale } from "../../../../utils";
import { PRODUCT_COLUMNS, ensureIndexColumn } from "../catalog";
import "./PropertyPanel.scss";

const i18n = Locale.getInstance();
const { Option } = Select;

function Field({ label, children }) {
  return (
    <div className="prop-field">
      <div className="prop-field__label">{label}</div>
      <div className="prop-field__control">{children}</div>
    </div>
  );
}

export default function PropertyPanel({
  component,
  inBesideStack = false,
  onChange,
  onCopy,
  onDelete,
  onEjectFromBeside,
  onJoinBeside,
}) {
  if (!component) {
    return (
      <div className="property-panel">
        <div className="property-panel__title">
          {i18n.t("order.printTemplate.props")}
        </div>
        <div className="property-panel__empty">
          {i18n.t("order.printTemplate.selectComponent")}
        </div>
      </div>
    );
  }

  const patch = (partial) => onChange?.({ ...component, ...partial });

  const addColumnMenu = {
    items: PRODUCT_COLUMNS.filter(
      (c) =>
        !c.locked &&
        !(component.columns || []).some((x) => x.key === c.key)
    ).map((c) => ({
      key: c.key,
      label: i18n.t(c.labelKey),
      onClick: () => {
        const cols = ensureIndexColumn([
          ...(component.columns || []).filter((x) => x.key !== "index"),
          {
            key: c.key,
            label: c.defaultLabel,
            width: c.defaultWidth,
          },
        ]);
        const total = cols.reduce((s, x) => s + (x.width || 10), 0) || 1;
        const normalized = cols.map((x) =>
          x.locked
            ? x
            : {
                ...x,
                width: Math.max(6, Math.round(((x.width || 10) / total) * 100)),
              }
        );
        patch({ columns: ensureIndexColumn(normalized) });
      },
    })),
  };

  const moveColumn = (idx, dir) => {
    const cols = ensureIndexColumn(component.columns || []);
    if (cols[idx]?.locked) return;
    const target = idx + dir;
    if (target <= 0 || target >= cols.length) return; // keep index at 0
    const next = [...cols];
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item);
    patch({ columns: ensureIndexColumn(next) });
  };

  return (
    <div className="property-panel">
      <div className="property-panel__title">
        {i18n.t("order.printTemplate.props")}
      </div>

      <div className="property-panel__section">
        <div className="property-panel__section-head">
          <span>{i18n.t("order.printTemplate.positionSize")}</span>
          <Space size={8}>
            <a onClick={onCopy}>{i18n.t("order.printTemplate.copy")}</a>
            <a onClick={onDelete}>{i18n.t("order.printTemplate.delete")}</a>
          </Space>
        </div>
        {inBesideStack ? (
          <div className="prop-field" style={{ marginBottom: 12 }}>
            <Button block onClick={onEjectFromBeside}>
              {i18n.t("order.printTemplate.ejectFromBeside")}
            </Button>
            <div className="prop-field__hint" style={{ marginTop: 6 }}>
              {i18n.t("order.printTemplate.ejectFromBesideHint")}
            </div>
          </div>
        ) : component.breakBeside ? (
          <div className="prop-field" style={{ marginBottom: 12 }}>
            <Button block onClick={onJoinBeside}>
              {i18n.t("order.printTemplate.joinBeside")}
            </Button>
          </div>
        ) : null}
        <div className="prop-grid">
          <Field label={i18n.t("order.printTemplate.startCol")}>
            <InputNumber
              min={1}
              max={12}
              value={component.startCol || 1}
              onChange={(v) => patch({ startCol: v })}
            />
          </Field>
          <Field label={i18n.t("order.printTemplate.span")}>
            <InputNumber
              min={1}
              max={12}
              value={component.span || 12}
              onChange={(v) => patch({ span: v })}
            />
          </Field>
        </div>
        <Field label={i18n.t("order.printTemplate.marginTop")}>
          <InputNumber
            min={0}
            value={component.marginTop || 0}
            onChange={(v) => patch({ marginTop: v })}
          />
        </Field>
      </div>

      {component.type === "custom-text" || component.type === "field" ? (
        <div className="property-panel__section">
          {component.type === "custom-text" ? (
            <Field label={i18n.t("order.printTemplate.textContent")}>
              <Input.TextArea
                rows={2}
                value={component.text || ""}
                onChange={(e) => patch({ text: e.target.value })}
              />
            </Field>
          ) : (
            <>
              <Field label={i18n.t("order.printTemplate.showLabel")}>
                <Switch
                  checked={component.showLabel !== false}
                  onChange={(v) => patch({ showLabel: v })}
                />
              </Field>
              <Field label={i18n.t("order.printTemplate.labelText")}>
                <Input
                  value={component.labelText || ""}
                  onChange={(e) => patch({ labelText: e.target.value })}
                />
              </Field>
            </>
          )}
          <div className="prop-grid">
            <Field label={i18n.t("order.printTemplate.fontSize")}>
              <InputNumber
                min={8}
                max={36}
                value={component.fontSize || 12}
                onChange={(v) => patch({ fontSize: v })}
              />
            </Field>
            <Field label={i18n.t("order.printTemplate.bold")}>
              <Checkbox
                checked={!!component.bold}
                onChange={(e) => patch({ bold: e.target.checked })}
              />
            </Field>
          </div>
          <Field label={i18n.t("order.printTemplate.align")}>
            <Select
              value={component.align || "left"}
              onChange={(v) => patch({ align: v })}
              style={{ width: "100%" }}
            >
              <Option value="left">{i18n.t("order.printTemplate.alignLeft")}</Option>
              <Option value="center">{i18n.t("order.printTemplate.alignCenter")}</Option>
              <Option value="right">{i18n.t("order.printTemplate.alignRight")}</Option>
            </Select>
          </Field>
        </div>
      ) : null}

      {component.type === "product-table" ? (
        <>
          <div className="property-panel__section">
            <Field label={i18n.t("order.printTemplate.tableType")}>
              <Select
                value={component.variant || "line"}
                onChange={(v) => patch({ variant: v })}
                style={{ width: "100%" }}
              >
                <Option value="line">
                  {i18n.t("order.printTemplate.comps.productLines")}
                </Option>
                <Option value="summary">
                  {i18n.t("order.printTemplate.comps.productSummary")}
                </Option>
              </Select>
            </Field>
            <Field label={i18n.t("order.printTemplate.showHeader")}>
              <Checkbox
                checked={component.showHeader !== false}
                onChange={(e) => patch({ showHeader: e.target.checked })}
              />
            </Field>
            <Field label={i18n.t("order.printTemplate.tableBorder")}>
              <Select
                value={component.border || "none"}
                onChange={(v) => patch({ border: v })}
                style={{ width: "100%" }}
              >
                <Option value="none">{i18n.t("order.printTemplate.borderNone")}</Option>
                <Option value="full">{i18n.t("order.printTemplate.borderFull")}</Option>
              </Select>
            </Field>
          </div>

          <div className="property-panel__section">
            <div className="property-panel__section-head">
              <span>{i18n.t("order.printTemplate.summaryRow")}</span>
            </div>
            <Field label={i18n.t("order.printTemplate.showSummary")}>
              <Checkbox
                checked={!!component.showSummary}
                onChange={(e) => patch({ showSummary: e.target.checked })}
              />
            </Field>
            <Field label={i18n.t("order.printTemplate.summaryLabel")}>
              <Input
                value={component.summaryLabel || ""}
                onChange={(e) => patch({ summaryLabel: e.target.value })}
              />
            </Field>
            <Field label={i18n.t("order.printTemplate.summaryColumns")}>
              <div className="prop-checks">
                {(component.columns || []).map((c) => (
                  <Checkbox
                    key={c.key}
                    checked={(component.summaryColumns || []).includes(c.key)}
                    onChange={(e) => {
                      const set = new Set(component.summaryColumns || []);
                      if (e.target.checked) set.add(c.key);
                      else set.delete(c.key);
                      patch({ summaryColumns: [...set] });
                    }}
                  >
                    {c.label}
                  </Checkbox>
                ))}
              </div>
            </Field>
          </div>

          <div className="property-panel__section">
            <div className="property-panel__section-head">
              <span>{i18n.t("order.printTemplate.headerRow")}</span>
            </div>
            <div className="prop-grid">
              <Field label={i18n.t("order.printTemplate.fontSize")}>
                <InputNumber
                  min={7}
                  max={18}
                  value={component.headerFontSize || 9}
                  onChange={(v) => patch({ headerFontSize: v })}
                />
              </Field>
              <Field label={i18n.t("order.printTemplate.bold")}>
                <Checkbox
                  checked={component.headerBold !== false}
                  onChange={(e) => patch({ headerBold: e.target.checked })}
                />
              </Field>
            </div>
            <div className="property-panel__section-head">
              <span>{i18n.t("order.printTemplate.bodyRow")}</span>
            </div>
            <div className="prop-grid">
              <Field label={i18n.t("order.printTemplate.fontSize")}>
                <InputNumber
                  min={7}
                  max={18}
                  value={component.bodyFontSize || 9}
                  onChange={(v) => patch({ bodyFontSize: v })}
                />
              </Field>
              <Field label={i18n.t("order.printTemplate.bold")}>
                <Checkbox
                  checked={!!component.bodyBold}
                  onChange={(e) => patch({ bodyBold: e.target.checked })}
                />
              </Field>
            </div>
          </div>

          <div className="property-panel__section">
            <div className="property-panel__section-head">
              <span>{i18n.t("order.printTemplate.columnConfig")}</span>
              <Dropdown menu={addColumnMenu} trigger={["click"]}>
                <Button size="small" icon={<PlusOutlined />}>
                  {i18n.t("order.printTemplate.addColumn")}
                </Button>
              </Dropdown>
            </div>
            <div className="prop-columns">
              {ensureIndexColumn(component.columns || []).map((col, idx) => {
                const locked = !!col.locked || col.key === "index";
                return (
                  <div
                    key={`${col.key}-${idx}`}
                    className={`prop-columns__row ${locked ? "is-locked" : ""}`}
                  >
                    <span className="prop-columns__ord">{idx + 1}</span>
                    {locked ? (
                      <Input
                        size="small"
                        value={col.label}
                        disabled
                        style={{ width: 110 }}
                      />
                    ) : (
                      <Select
                        value={col.key}
                        style={{ width: 110 }}
                        size="small"
                        onChange={(key) => {
                          const meta = PRODUCT_COLUMNS.find((c) => c.key === key);
                          const cols = ensureIndexColumn(component.columns || []);
                          cols[idx] = {
                            ...cols[idx],
                            key,
                            label: meta?.defaultLabel || key,
                            locked: false,
                          };
                          patch({ columns: ensureIndexColumn(cols) });
                        }}
                      >
                        {PRODUCT_COLUMNS.filter((c) => !c.locked).map((c) => (
                          <Option key={c.key} value={c.key}>
                            {i18n.t(c.labelKey)}
                          </Option>
                        ))}
                      </Select>
                    )}
                    {!locked && (
                      <Input
                        size="small"
                        value={col.label}
                        style={{ flex: 1 }}
                        onChange={(e) => {
                          const cols = ensureIndexColumn(component.columns || []);
                          cols[idx] = { ...cols[idx], label: e.target.value };
                          patch({ columns: cols });
                        }}
                      />
                    )}
                    {locked && <span className="prop-columns__flex" />}
                    <InputNumber
                      size="small"
                      min={4}
                      max={80}
                      value={col.width}
                      addonAfter="%"
                      style={{ width: 88 }}
                      onChange={(v) => {
                        const cols = ensureIndexColumn(component.columns || []);
                        cols[idx] = { ...cols[idx], width: v };
                        patch({ columns: cols });
                      }}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={locked || idx <= 1}
                      onClick={() => moveColumn(idx, -1)}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={locked || idx >= ensureIndexColumn(component.columns || []).length - 1}
                      onClick={() => moveColumn(idx, 1)}
                    />
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={locked}
                      onClick={() => {
                        if (locked) return;
                        const cols = ensureIndexColumn(component.columns || []).filter(
                          (_, i) => i !== idx
                        );
                        patch({ columns: ensureIndexColumn(cols) });
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : null}

      {component.type === "image" ? (
        <div className="property-panel__section">
          <Field label={i18n.t("order.printTemplate.imageUrl")}>
            <Input
              value={component.url || ""}
              onChange={(e) => patch({ url: e.target.value })}
              placeholder="https://"
            />
          </Field>
          <Field label={i18n.t("order.printTemplate.height")}>
            <InputNumber
              min={20}
              value={component.height || 48}
              onChange={(v) => patch({ height: v })}
            />
          </Field>
          {(component.span || 12) < 12 ? (
            <Field label={i18n.t("order.printTemplate.besideRows")}>
              <InputNumber
                min={0}
                max={20}
                value={component.besideRows == null ? 2 : component.besideRows}
                onChange={(v) => patch({ besideRows: v })}
              />
            </Field>
          ) : null}
        </div>
      ) : null}

      {component.type === "qrcode" || component.type === "barcode" ? (
        <div className="property-panel__section">
          {component.field === "custom" ? (
            <Field label={i18n.t("order.printTemplate.customValue")}>
              <Input
                value={component.value || ""}
                onChange={(e) => patch({ value: e.target.value })}
              />
            </Field>
          ) : null}
          {component.type === "barcode" ? (
            <Field label={i18n.t("order.printTemplate.barcodeFormat")}>
              <Select
                value={
                  component.barcodeFormat ||
                  (component.field === "order_no" ? "qr" : "code128")
                }
                onChange={(v) =>
                  patch({
                    barcodeFormat: v,
                    height: v === "qr" ? 120 : 48,
                    size: v === "qr" ? 120 : component.size,
                  })
                }
              >
                <Option value="qr">
                  {i18n.t("order.printTemplate.barcodeFormatQr")}
                </Option>
                <Option value="code128">
                  {i18n.t("order.printTemplate.barcodeFormatCode128")}
                </Option>
              </Select>
            </Field>
          ) : null}
          {component.type === "qrcode" ||
          (component.type === "barcode" &&
            (component.barcodeFormat ||
              (component.field === "order_no" ? "qr" : "code128")) ===
              "qr") ? (
            <Field label={i18n.t("order.printTemplate.qrSize")}>
              <InputNumber
                min={64}
                max={220}
                value={component.size || component.height || 120}
                onChange={(v) => patch({ size: v, height: v })}
              />
            </Field>
          ) : component.type === "barcode" ? (
            <Field label={i18n.t("order.printTemplate.height")}>
              <InputNumber
                min={32}
                max={120}
                value={component.height || 48}
                onChange={(v) => patch({ height: v })}
              />
            </Field>
          ) : null}
          {(component.type === "qrcode" ||
            component.type === "image" ||
            (component.type === "barcode" &&
              (component.barcodeFormat ||
                (component.field === "order_no" ? "qr" : "code128")) ===
                "qr")) &&
          (component.span || 12) < 12 ? (
            <>
              <Field label={i18n.t("order.printTemplate.besideRows")}>
                <InputNumber
                  min={0}
                  max={20}
                  value={
                    component.besideRows == null ? 3 : component.besideRows
                  }
                  onChange={(v) => patch({ besideRows: v })}
                />
              </Field>
              <div className="prop-field__hint">
                {i18n.t("order.printTemplate.besideRowsHint")}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
